/**
 * @file time-zone-options.ts
 * @description Список часовых поясов для формы магазина в кабинете платформы
 * (phase19.28 этап А; SC58, PL4).
 *
 * Справочник с русскими названиями: `GET /admin/platform/time-zones`
 * (кабинет платформы); нет маршрута (старый API) — `/admin/shop/time-zones`
 * (нужен кабинет магазина); не ответили оба — список из
 * `Intl.supportedValuesOf`: IANA-имя и смещение, без русских названий
 * (сервер всё равно проверит значение и ответит `shop_time_zone_invalid`).
 */

import { platformShops } from "../api-platform-shops";
import { listShopTimeZones, type ShopTimeZoneOption } from "../api-shop-settings";
import { timeZoneOffsetLabel } from "../shop-time";

/** «UTC+5» → «+05:00», как у справочника сервера. */
function offsetOf(zone: string, now: Date): { offset: string; minutes: number } {
  const label = timeZoneOffsetLabel(zone, now);
  const match = /UTC([+−])(\d{1,2})(?::(\d{2}))?/.exec(label);
  if (!match) return { offset: "+00:00", minutes: 0 };
  const sign = match[1] === "−" ? -1 : 1;
  const hours = Number(match[2]);
  const mins = Number(match[3] ?? "0");
  const text = `${sign < 0 ? "-" : "+"}${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
  return { offset: text, minutes: sign * (hours * 60 + mins) };
}

/** Запасной список поясов из рантайма браузера. */
export function intlTimeZoneOptions(now: Date = new Date()): ShopTimeZoneOption[] {
  const supported = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [];
  const zones = supported.length > 0 ? supported : ["UTC"];
  return zones
    .map((id) => {
      const { offset, minutes } = offsetOf(id, now);
      // Без русских названий группу «Россия» не выделяем — словарь живёт на сервере.
      return { id, label: id, offset, offsetMinutes: minutes, group: "Другие" as const };
    })
    .sort((a, b) => a.offsetMinutes - b.offsetMinutes || a.id.localeCompare(b.id));
}

/** Первый непустой ответ из источников; все отказали — null. */
async function firstList(sources: ReadonlyArray<() => Promise<ShopTimeZoneOption[]>>): Promise<ShopTimeZoneOption[] | null> {
  for (const load of sources) {
    try {
      const list = await load();
      if (list.length > 0) return list;
    } catch {
      // Источник недоступен — пробуем следующий.
    }
  }
  return null;
}

/** Справочник платформы → справочник магазина → запасной из браузера. */
export async function loadTimeZoneOptions(): Promise<ShopTimeZoneOption[]> {
  return (await firstList([platformShops.timeZones, listShopTimeZones])) ?? intlTimeZoneOptions();
}
